<template>
  <main class="flex items-center justify-center h-screen bg-app-gray-900">
    <img alt="MetaMask logo" :src="DEFAULT_WEB3AUTH_BY_METAMASK_LOGO" class="absolute left-9 top-9 h-8 w-auto invert" />
    <div class="w-[800px]">
      <div class="text-3xl font-bold leading-tight mb-5 text-center text-app-white">Let's set up your dashboard account</div>
      <Card class="h-auto px-12 py-16">
        <div class="mb-6 text-2xl font-extrabold leading-tight">Personal Information</div>
        <div class="grid grid-cols-2 gap-5 mb-3">
          <TextField
            v-model="state.name"
            label="Name*"
            placeholder="e.g. Bonnie Green"
            :error="v$.name.$error"
            :helper-text="v$.name.$error ? (v$.name.$errors[0].$message as string) : ' '"
            @input="v$.name.$validate()"
          />
          <TextField
            v-model="state.email"
            label="Email*"
            placeholder="email@example.com"
            :disabled="hasSocialLoginEmail"
            :error="v$.email.$error"
            :helper-text="v$.email.$error ? (v$.email.$errors[0].$message as string) : ' '"
            @input="v$.email.$validate()"
          />
        </div>
        <div class="my-5">
          <Checkbox id="create-profile-tac" v-model:checked="acceptedTermsAndPrivacyPolicy" class="text-sm">
            <template #checkboxLabel>
              By signing up, you confirm you have read and agree to MetaMask Developer's
              <a class="text-app-primary-500 underline" :href="config.consensysTermsOfUse" target="_blank" rel="noopener noreferrer">Terms of Use</a>
              and
              <a class="text-app-primary-500 underline" :href="config.consensysPrivacyPolicy" target="_blank" rel="noopener noreferrer">
                Privacy Policy.
              </a>
            </template>
          </Checkbox>
        </div>
        <Button :disabled="v$.$invalid || !acceptedTermsAndPrivacyPolicy" :loading="creatingProfile" block @click="createProfile">Next</Button>
        <div v-if="error" class="text-app-error">{{ error }}</div>
      </Card>
    </div>
  </main>
</template>

<script setup lang="ts">
import { Checkbox } from "@toruslabs/vue-components/Checkbox";
import useVuelidate from "@vuelidate/core";
import { email, helpers, required } from "@vuelidate/validators";
import { computed, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { TextField } from "@/components/TextField";
import config from "@/config";
import log from "@/helpers/log";
import { userModule } from "@/store";
import { DEFAULT_WEB3AUTH_BY_METAMASK_LOGO } from "@/utils/enums";

const route = useRoute();
const router = useRouter();

const state = reactive({
  name: userModule.userInfoGetter?.name ?? "",
  email: userModule.userInfoGetter?.email ?? "",
});
const rules = {
  name: { required: helpers.withMessage("This field is required", required) },
  email: { required: helpers.withMessage("This field is required", required), email },
};
const v$ = useVuelidate(rules, state);

const hasSocialLoginEmail = computed(() => Boolean(userModule.userInfoGetter?.email));

const acceptedTermsAndPrivacyPolicy = ref(false);
const error = ref("");

const creatingProfile = ref(false);

const isInvitationLink = computed(() => {
  const { query = {} } = route;
  return Boolean(query.invite_code);
});

const createProfile = async () => {
  creatingProfile.value = true;
  const valid = await v$.value.$validate();
  if (!valid) return;
  try {
    await userModule.createProfile({
      name: state.name,
      email: state.email,
    });

    const { query = {} } = route;
    if (isInvitationLink.value) {
      router.replace({ name: "accept-invite", query });
      return;
    }

    router.push({ name: "create-organization" });
  } catch (e) {
    error.value = (e as Error).message;
    log.error("Failed to create user profile", e);
  } finally {
    creatingProfile.value = false;
  }
};
</script>
